import { mutate } from 'swr';
import {
    createMutation,
    getAsync,
    mutateAsync,
    simpleGet,
} from '../hooks/useApi';
import {
    CreateTenantRequest,
    KintoneLogsModel,
    TenantModel,
    TenantOptionsModel,
    UpdateTenantRequest,
} from '../models/TenantModel';
import { HttpClient } from '../utils/HttpUilts';
import { safeUrl } from '../utils/safeUrl';
import { useAuth } from '../contexts/AuthContext';

export const tenantApi = {
    get: {
        getKintoneLogsForAdmin: (
            tenantId: string,
            appId?: string,
            offset: number = 0,
            limit: number = 100
        ) => {
            const params = new URLSearchParams();
            params.append('limit', String(limit));
            params.append('offset', offset.toString());
            return simpleGet<KintoneLogsModel>`/admin/tenant/${tenantId}/kintone/logs/${appId}?${params}`;
        },
        getTenantsForSuperAdmin: simpleGet<TenantModel[]>`/tenants`,
        getTenantForSuperAdmin: (tenantId: string) =>
            simpleGet<TenantModel>`/tenants/${tenantId}`,
        getTenantStatus: simpleGet<TenantModel>`/tenant/status`,
        getTenantUsersForSuperAdmin: (tenantId: string) =>
            simpleGet<TenantModel[]>`/tenants/${tenantId}/users`,
        kintoneOptions: (tenantId: string) =>
            tenantApi.get.options({
                tenantId,
                category: 'kintone',
                keys: ['id', 'pass', 'endpoint'],
            }),
        options: ({
            tenantId,
            category,
            array,
            keys,
        }: GetTenantOptionModel) => {
            const q = new URLSearchParams();
            q.append('tenantId', tenantId);
            q.append('category', category);
            for (const key of keys ?? []) {
                q.append('keys[]', key);
            }
            for (const val of array ?? []) {
                q.append('array[]', val);
            }
            return simpleGet<TenantOptionsModel>`/admin/tenant/${tenantId}/options?${q}`;
        },
    },
    mutate: {
        createTenantForSuperAdmin: createMutation<
            CreateTenantRequest,
            {
                status: string;
                data: TenantModel;
            }
        >({
            mutate: async ({ authenticated }, data, opts) =>
                authenticated.post('/tenant/create', data, opts),
            effect() {
                mutate(tenantApi.get.getTenantsForSuperAdmin.key);
            },
        }),
        updateTenantForAdmin: createMutation<
            UpdateTenantRequest,
            {
                status: string;
                data: TenantModel;
            }
        >({
            mutate: async ({ authenticated }, data, opts) =>
                authenticated.put('/tenant/update', data, opts),
            effect({ data }) {
                mutate(tenantApi.get.getTenantStatus.key);
                mutate(tenantApi.get.getTenantsForSuperAdmin.key);
            },
        }),
        updateTenantOption: createMutation<
            {
                tenantId: string;
                options: TenantOptionsModel;
            },
            {}
        >({
            mutate: async ({ authenticated }, { tenantId, options }, opts) => {
                const all = await Promise.allSettled(
                    Object.entries(options).map(([key, value]) =>
                        authenticated.put(
                            `/admin/tenant/options`,
                            {
                                tenantId,
                                options: {
                                    [key]: value,
                                },
                            },
                            opts
                        )
                    )
                );
                const errors = all
                    .filter((res) => res.status === 'rejected')
                    .map((res) => res.reason);
                if (errors.length > 0) {
                    console.error(errors);
                    throw new Error(``);
                }
                return {
                    data: {},
                    status: 200,
                };
            },
            effect({}, { tenantId }) {
                tenantApi.get.getTenantsForSuperAdmin.mutate();
                mutate((key) => {
                    if (typeof key !== 'string') return;
                    return key.startsWith(`/admin/tenant/${tenantId}/`);
                });
            },
        }),
    },
};
//*** */

export const getTenantsForSuperAdmin = (): Promise<{ data: TenantModel[] }> => {
    return HttpClient.get('/tenants');
};

export const createTenantForSuperAdmin = (data: CreateTenantRequest) => {
    return HttpClient.post('/tenant/create', data);
};

export const updateTenantForAdmin = (data: UpdateTenantRequest) => {
    return HttpClient.put('/tenant/update', data);
};

export const getTenantForSuperAdmin = (tenantId: string) => {
    return HttpClient.get<{ data: TenantModel }>(safeUrl`/tenant/${tenantId}`);
};

export const getTenantStorageUsagesForSuperAdmin = (
    tenantId: string,
    limit: number
) => {
    return HttpClient.get(`/admin/tenant/${tenantId}/storage/usages`, {
        limit,
    });
};

//*** */
export const getTenantAiTypes = (tenantId: string) => {
    return HttpClient.get(
        safeUrl`/admin/ai-analysis-types-tenants/${tenantId}`
    );
};

export type GetTenantOptionModel = {
    tenantId: string;
    category: string;
    keys?: Array<string>;
    array?: Array<string>;
};

export const getTenantOptions = (params: GetTenantOptionModel) =>
    getAsync(tenantApi.get.options(params));

export const getIsExternalApi = () => {
    return HttpClient.get(`/tenant/options/isExternalApi`);
};

export const getTenantForKintoneAppFields = (
    tenantId: string,
    appId: number
) => {
    return HttpClient.get(
        safeUrl`/admin/tenant/${tenantId}/options/kintone/${appId.toString()}/fields`
    );
};

export const getKintoneAppStatus = (appId: number) => {
    return HttpClient.get(safeUrl`/admin/kintone/${appId.toString()}/status`);
};

export const getTenantForKintoneApp = (tenantId: string, appId: number) => {
    return HttpClient.get(
        safeUrl`/admin/tenant/${tenantId}/options/kintone/app/${appId.toString()}`
    );
};

export const putTenantOptions = (
    tenantId: string,
    options: TenantOptionsModel | null
) => {
    return mutateAsync(tenantApi.mutate.updateTenantOption, {
        tenantId,
        options,
    });
};
